<template>
  <v-col
    cols="12"
    class="stay-page"
  >
    <v-card>
      <v-card-title class="align-center">
        Séjours
        <v-spacer></v-spacer>
        <v-btn
          small
          color="info"
          :loading="loading"
          @click="refresh"
        >
          <v-icon
            small
            class="mr-1"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          Actualiser
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div class="stay-meta mb-5">
          Moteur public 704 · Hors prix d'appel
        </div>
        <v-alert
          v-if="refreshError"
          type="error"
          outlined
          class="stay-error"
        >
          {{ refreshError }}
        </v-alert>
        <v-progress-linear
          v-if="loading && !summary"
          indeterminate
          color="info"
          aria-label="Chargement des statistiques"
        ></v-progress-linear>
        <section
          class="stay-metrics"
          aria-label="Statistiques des séjours"
        >
          <div
            v-for="metric in metrics"
            :key="metric.label"
            class="stay-metric"
          >
            <div class="text-caption text-uppercase">
              {{ metric.label }}
            </div>
            <div class="stay-metric-value text-h6 font-weight-bold">
              {{ formatNumber(metric.value) }}
            </div>
          </div>
        </section>

        <section
          v-if="summary"
          class="stay-band"
        >
          <div class="stay-splits">
            <div>
              <h2>Durées des séjours</h2>
              <div class="stay-distribution">
                <div
                  v-for="row in summary.inventory.byDuration"
                  :key="String(row.duration)"
                  class="stay-distribution-item"
                >
                  <span>{{ row.duration === null ? 'Non renseignée' : row.duration + (row.duration === 1 ? ' nuit' : ' nuits') }}</span>
                  <strong class="stay-number">{{ formatNumber(row.count) }}</strong>
                </div>
                <span
                  v-if="!summary.inventory.byDuration.length"
                  class="stay-meta"
                >Aucun séjour en base</span>
              </div>
            </div>
            <div>
              <h2>Répartition</h2>
              <div class="stay-distribution">
                <div
                  v-for="row in summary.inventory.byType"
                  :key="String(row.type)"
                  class="stay-distribution-item"
                >
                  <span>{{ row.type === 1 ? 'Locatifs' : row.type === 2 ? 'Emplacements' : 'Autres' }}</span>
                  <strong class="stay-number">{{ formatNumber(row.count) }}</strong>
                </div>
              </div>
              <div class="stay-meta mt-4">
                Arrivées à partir du {{ formatDay(summary.inventory.firstArrival) }} · Dernier départ le {{ formatDay(summary.inventory.lastDeparture) }}
              </div>
            </div>
          </div>
          <div class="stay-meta mt-4">
            Statistiques au {{ formatDate(summary.inventory.computedAt) }} · Heure de Paris
          </div>
        </section>

        <section class="stay-band">
          <div class="stay-heading mb-0">
            <h2 class="mb-0">
              Sessions d'import
            </h2>
            <div
              v-if="summary"
              class="stay-distribution stay-meta"
            >
              <span>{{ formatNumber(summary.sessions.active) }} en cours</span>
              <span>{{ formatNumber(summary.sessions.completed) }} terminées</span>
              <span>{{ formatNumber(summary.sessions.withErrors) }} avec erreurs de traitement</span>
            </div>
          </div>
          <div class="stay-session-filter">
            <v-select
              v-model="status"
              :items="statuses"
              label="État des sessions"
              outlined
              dense
              hide-details
              @change="filtersChanged"
            ></v-select>
          </div>
          <v-data-table
            :headers="headers"
            :items="sessions"
            :loading="loading"
            :items-per-page="25"
            hide-default-footer
            disable-sort
            class="stay-table"
            no-data-text="Aucune session ne correspond à ce filtre."
            loading-text="Chargement des sessions…"
          >
            <template v-slot:item.createdAt="{ item }">
              <router-link :to="{ name: 'staySessionDetails', params: { runId: item.id } }">
                {{ formatDate(item.createdAt) }}
              </router-link>
              <div class="stay-id mt-1">
                {{ item.id }}
              </div>
              <div class="stay-meta">
                {{ item.selection.all ? 'Tous les campings' : 'Sélection ciblée' }} · {{ formatNumber(item.campingCount) }} campings
              </div>
            </template>
            <template v-slot:item.downloadStatus="{ item }">
              <StatusBadge :status="item.downloadStatus"></StatusBadge>
              <div class="stay-progress mt-2">
                <div class="stay-meta">
                  {{ item.downloadFinishedCount }} / {{ item.campingCount }} traités
                </div>
                <v-progress-linear
                  :value="percentage(item.downloadFinishedCount, item.campingCount)"
                  :color="statusColor(item.downloadStatus)"
                  height="4"
                  :aria-label="'Collecte : ' + item.downloadFinishedCount + ' sur ' + item.campingCount"
                ></v-progress-linear>
              </div>
            </template>
            <template v-slot:item.importStatus="{ item }">
              <StatusBadge :status="item.importStatus"></StatusBadge>
              <div class="stay-progress mt-2">
                <div class="stay-meta">
                  {{ item.importFinishedCount }} / {{ item.campingCount }} traités
                </div>
                <v-progress-linear
                  :value="percentage(item.importFinishedCount, item.campingCount)"
                  :color="statusColor(item.importStatus)"
                  height="4"
                  :aria-label="'Import : ' + item.importFinishedCount + ' sur ' + item.campingCount"
                ></v-progress-linear>
              </div>
            </template>
            <template v-slot:item.publishedCount="{ item }">
              <span class="stay-number">{{ formatNumber(item.publishedCount) }}</span>
            </template>
            <template v-slot:item.issues="{ item }">
              <div
                v-if="item.failedCampingCount"
                class="error--text"
              >
                {{ item.failedCampingCount }} en échec
              </div>
              <div
                v-if="item.retryCampingCount"
                class="warning--text"
              >
                {{ item.retryCampingCount }} à réessayer
              </div>
              <div
                v-if="item.emptyCampingCount"
                class="stay-meta"
              >
                {{ item.emptyCampingCount }} sans séjour
              </div>
              <span v-if="!item.failedCampingCount && !item.retryCampingCount && !item.emptyCampingCount">—</span>
            </template>
            <template v-slot:item.filesPurgedAt="{ item }">
              <span :class="item.filesPurgeError ? 'error--text' : ''">{{ item.filesPurgeError ? 'Erreur de nettoyage' : item.filesPurgedAt ? 'Supprimés' : item.filesPurgeStartedAt ? 'Suppression inachevée' : 'Conservés' }}</span>
              <div
                v-if="item.filesPurgedAt"
                class="stay-meta"
              >
                {{ formatDate(item.filesPurgedAt) }}
              </div>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    :to="{ name: 'staySessionDetails', params: { runId: item.id } }"
                    aria-label="Consulter la session"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>{{ icons.mdiChevronRight }}</v-icon>
                  </v-btn>
                </template>
                <span>Consulter la session</span>
              </v-tooltip>
            </template>
          </v-data-table>
          <div class="stay-footer">
            <span class="stay-meta">{{ formatNumber(total) }} sessions · Suivi au {{ formatDate(lastUpdated) }}</span>
            <v-pagination
              v-if="total > 25"
              v-model="page"
              :length="Math.ceil(total / 25)"
              :total-visible="5"
              color="info"
              @input="refresh"
            ></v-pagination>
          </div>
        </section>
      </v-card-text>
    </v-card>
  </v-col>
</template>

<script>
import { mdiRefresh, mdiChevronRight } from '@mdi/js'
import monitoring from './monitoring'
import StatusBadge from './StatusBadge.vue'

export default {
  components: { StatusBadge },
  mixins: [monitoring],
  data: () => ({
    summary: null, sessions: [], total: 0, page: 1, status: 'all',
    icons: { mdiRefresh, mdiChevronRight },
    statuses: [{ text: 'Toutes', value: 'all' }, { text: 'En cours', value: 'active' }, { text: 'Terminées sans erreur', value: 'completed' }, { text: 'Erreurs et reprises', value: 'errors' }],
    headers: [{ text: 'Session', value: 'createdAt' }, { text: 'Collecte', value: 'downloadStatus' },
      { text: 'Import', value: 'importStatus' }, { text: 'Séjours validés', value: 'publishedCount', align: 'end' },
      { text: 'Campings à signaler', value: 'issues' }, { text: 'Fichiers', value: 'filesPurgedAt' }, { text: '', value: 'actions', width: 48 }]
  }),
  computed: {
    metrics () {
      const stats = this.summary ? this.summary.inventory : {}

      return [{ label: 'Séjours en base', value: stats.stayCount }, { label: 'Campings avec séjours', value: stats.campingCount },
        { label: 'Séjours à venir', value: stats.upcomingStayCount }, { label: 'Séjours avec réduction', value: stats.discountedStayCount }]
    }
  },
  methods: {
    async loadData (token) {
      const summary = await this.getMonitoring('/summary', token)
      const result = await this.getMonitoring('/sessions', token, { page: this.page, limit: 25, status: this.status })

      return { summary, sessions: result.items, total: result.total }
    },
    filtersChanged () {
      this.page = 1
      this.refresh()
    }
  }
}
</script>

<style lang="scss" src="./stay.scss"></style>
